import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Flame, Check, FileText, ArrowRight } from "lucide-react";

// Prezzi fissi 8energy
const PREZZI = {
  luce_spread: "0.013000",
  luce_comm: "102,00",
  gas_spread: "0.110000", 
  gas_comm: "102,00",
  data_scadenza: "30 Aprile 2026"
};

// Link documenti
const DOCS = {
  cte_luce: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  cte_gas: "https://709795ed-3522-4b5b-a519-c233de9f5112.usrfiles.com/ugd/709795_2c985f24aadd44928cba0664efa18300.pdf",
  condizioni: "https://customer-assets.emergentagent.com/job_f56f21f7-c17f-4649-a9ca-6cb78c28c69d/artifacts/4adzcav1_Condizioni-Generali2722026.pdf"
};

export default function OffersBanner({ tipo = "both", showCta = true }) {
  return (
    <div className="rounded-[2rem] bg-gradient-to-br from-[#f8fff0] to-white border border-[#ADFF2F]/30 p-6 md:p-8" data-testid="offers-banner">
      {/* Promo Badge */}
      <div className="flex justify-center mb-6">
        <div 
          className="inline-flex items-center gap-3 bg-white px-5 py-2 rounded-full border-2 border-[#ADFF2F] font-bold text-sm shadow-lg shadow-[#ADFF2F]/20"
          style={{ animation: 'pulse 2s infinite' }}
        >
          <Zap className="w-4 h-4 text-[#006838]" strokeWidth={2} />
          <span className="text-[#006838]">PROMO PLUS ONLINE</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">Fino al {PREZZI.data_scadenza}</span>
        </div>
      </div>

      {/* Offers Grid */}
      <div className={`grid grid-cols-1 ${tipo === "both" ? "md:grid-cols-2" : ""} gap-6 mb-6`}>
        {(tipo === "both" || tipo === "luce") && (
          <OfferCard
            type="luce"
            title="Luce Plus Online"
            subtitle="Luce Domestico"
            formula="PUN"
            spread={PREZZI.luce_spread}
            unit="€/kWh"
            commission={PREZZI.luce_comm}
            features={["Energia 100% Green", "Switch 100% Online"]}
            cteLink={DOCS.cte_luce}
            conditionsLink={DOCS.condizioni}
          />
        )}
        {(tipo === "both" || tipo === "gas") && (
          <OfferCard
            type="gas"
            title="Gas Plus Online"
            subtitle="Gas Domestico"
            formula="PSV"
            spread={PREZZI.gas_spread}
            unit="€/Smc"
            commission={PREZZI.gas_comm}
            features={["Prezzo Trasparente", "Switch 100% Online"]}
            cteLink={DOCS.cte_gas}
            conditionsLink={DOCS.condizioni}
          />
        )}
      </div>

      {/* CTA */}
      {showCta && (
        <div className="text-center">
          <Link to="/switch-guide">
            <button 
              className="inline-flex items-center gap-3 bg-[#006838] text-white px-8 py-4 rounded-full font-bold text-base shadow-lg shadow-[#006838]/25 hover:bg-[#ADFF2F] hover:text-[#006838] hover:-translate-y-1 transition-all duration-300"
              data-testid="offers-banner-cta"
            >
              ATTIVA ORA
              <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </Link>
          <p className="text-xs text-gray-400 mt-4">
            *I prezzi si riferiscono alla componente energia e materia prima. IVA e imposte escluse.
          </p>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>
    </div>
  );
}

function OfferCard({ type, title, subtitle, formula, spread, unit, commission, features, cteLink, conditionsLink }) {
  const isLuce = type === "luce";
  const color = isLuce ? "#006838" : "#FF6B00";
  
  return (
    <motion.div 
      className="relative rounded-2xl bg-white border border-gray-100 p-5 hover:border-[#ADFF2F] hover:shadow-lg transition-all duration-300"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`offer-banner-card-${type}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLuce ? "bg-amber-50" : "bg-orange-50"}`}>
          {isLuce ? <Zap className="w-5 h-5 text-amber-500" strokeWidth={1.5} /> : <Flame className="w-5 h-5 text-orange-500" strokeWidth={1.5} />}
        </div>
        <div>
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color }}>{subtitle}</span>
          <h3 className="font-bold text-gray-900">{title}</h3>
        </div>
      </div>
      
      {/* Price Box */}
      <div className="bg-[#f5f5f7] rounded-xl p-4 text-center mb-4">
        <span className="text-gray-500 text-sm">{formula} + </span>
        <span className="text-xl font-bold text-[#006838]">{spread}</span>
        <span className="text-lg font-bold text-[#006838]"> {unit}*</span>
      </div>
      
      {/* Features */}
      <ul className="space-y-2 mb-4">
        <li className="flex items-center gap-2 text-sm text-gray-600">
          <Check className="w-4 h-4 text-[#006838]" strokeWidth={2} />
          Comm.ne: €{commission}/anno
        </li>
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-[#006838]" strokeWidth={2} />
            {feature}
          </li>
        ))}
      </ul>
      
      {/* Document Links */}
      <div className="flex gap-2">
        <a
          href={cteLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-gray-100 text-xs font-semibold text-gray-600 hover:text-[#006838] hover:border-[#ADFF2F] transition-all"
        >
          <FileText className="w-3 h-3" strokeWidth={2} />
          CTE
        </a>
        <a
          href={conditionsLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg border border-gray-100 text-xs font-semibold text-gray-600 hover:text-[#006838] hover:border-[#ADFF2F] transition-all"
        >
          <FileText className="w-3 h-3" strokeWidth={2} />
          Condizioni
        </a>
      </div>
    </motion.div>
  );
}
